import React, { useEffect, useState } from "react";
import { Text } from "@vibe/core";
import { Modal, ModalHeader, ModalContent, ModalFooter, ModalBasicLayout } from "@vibe/core/next";
import logger from "../../lib/logger";
import {
  resyncSubitemColumn,
  resyncSubitem,
  resyncSubitemName,
} from "../../utils/requests/subitemDiscrepancyRequests";
import type { SubitemScanRow } from "./__fixtures__/discrepancyFixtures";
import type { BoardHeader } from "./DiscrepancyGrid/discrepancyGrid.interface";
import ResyncPreviewTable, {
  ResyncPreview,
  previewActionLabel,
  previewTargets,
} from "./ResyncPreviewTable";
import ResyncOutcome, { ResyncFailureLine } from "./ResyncOutcome";
import { boardLabel } from "./resyncCopy";

// Mirrors the engine's ResyncResult (simb-integration-ms's subitem-resync.interface.ts) —
// not imported, since that package is a separate service with its own build, but the shape
// is pinned to it. `succeeded` and `failed` are NOT mutually exclusive: resyncSubitem batches
// every drifting column of one subitem into one mutation per board, so the same board can
// land a write (succeeded) while one of its OTHER columns was refused before entering that
// batch (failed, with that column's key). `columnKey` present => a per-column refusal;
// absent => board-level. `reason` is already human-readable and safe to show verbatim.
export interface ResyncResult {
  succeeded: string[];
  failed: Array<{ boardId: string; columnKey?: string; reason: string }>;
}

/**
 * What this modal renders after a confirm. A bulk ("Sync all") request spans TWO endpoints —
 * `resync-subitem` for the columns and `resync-name` for the name — so a result can be partial
 * in a way a single ResyncResult cannot express: one call can reject outright while the other
 * returns per-board detail. `rejected` carries a human sentence per whole-request rejection,
 * for which there is no per-board detail to report. An entry here means part of what the user
 * previewed did NOT happen, and must never be rendered as though it had.
 */
export interface ModalResult extends ResyncResult {
  rejected: string[];
}

type ValueEntry = SubitemScanRow["valuesByBoard"][string];

/**
 * The engine's synthetic name row (subitem-classifier.ts's NAME_COLUMN_KEY). It is NOT a
 * column: its `columnIdsByBoard` is empty by design, so `resync-subitem`'s `buildColumnWrites`
 * — which iterates exactly those keys — produces zero writes for it and `resync-subitem` has no
 * name path at all (`namesByBoard` appears only in `resyncName`). Sending it there is a silent
 * discard, acknowledged as such in the service's own comment at subitem-resync.service.ts:678,
 * which is why the bulk path below splits it out and calls `resync-name` for it instead.
 */
const NAME_COLUMN_KEY = "__name__";

/**
 * Whether a row cannot be written at all, so the preview must not promise it.
 *
 * `translateColumnValue` refuses these 100% of the time — Monday exposes no usable value for
 * board_relation/dependency/subtasks, and writing the read shape back would instruct Monday to
 * CLEAR the target's links — so previewing "this will sync Related items" and offering Confirm
 * promises something that cannot happen, with the reason arriving only after the user commits.
 *
 * READS THE ENGINE'S ANSWER. This used to be a hand-mirrored copy of the engine's
 * UNWRITABLE_COLUMN_TYPES, justified in a comment on the grounds that the two services build
 * separately — the same justification `pickWinningBoard` carried, and it failed the same silent
 * way once the other copy moved. A cross-repo test cannot guard that: the repos are checked out
 * independently, so a test reading the sibling repo passes vacuously wherever only one exists.
 * So the rule travels on the row (`ScanRow.writable`) and only the WORDING lives here.
 *
 * Compares against `false` explicitly rather than negating: a row from a pre-`writable` engine
 * has the field undefined, and treating that as unwritable would silently refuse to preview
 * every column. Undefined means "not told", which is the behaviour that shipped before this
 * field existed.
 *
 * Still a preview gate ONLY: the backend's own `reason` is rendered verbatim if a request is
 * made anyway (Plan Decision 2c) — see the result branch below.
 */
const isUnwritableRow = (row: SubitemScanRow): boolean => row.writable === false;

export interface SubitemResyncColumnRequest {
  kind: "column";
  parentSyncId: string;
  subitemSyncId: string;
  columnKey: string;
  row: SubitemScanRow;
  boards: BoardHeader[];
}

export interface SubitemResyncNameRequest {
  kind: "name";
  parentSyncId: string;
  subitemSyncId: string;
  /** The subitem's `__name__` row. */
  row: SubitemScanRow;
  boards: BoardHeader[];
}

export interface SubitemResyncSubitemRequest {
  kind: "subitem";
  parentSyncId: string;
  subitemSyncId: string;
  subitemName: string;
  /**
   * Every row of the subitem, including the synthetic `__name__` row and the `ok` rows — the
   * controller filters `ok` rows itself. This modal splits `__name__` back out before sending
   * (see `handleConfirm`): `resync-subitem` cannot write a name, so that row goes to
   * `resync-name` instead rather than being sent somewhere it is silently dropped.
   */
  rows: SubitemScanRow[];
  boards: BoardHeader[];
}

export type SubitemResyncRequest =
  | SubitemResyncColumnRequest
  | SubitemResyncNameRequest
  | SubitemResyncSubitemRequest;

interface SubitemResyncModalProps {
  request: SubitemResyncRequest | null;
  token: string | null;
  onClose: () => void;
  /** Called when a refetch could return something new, so the caller can revalidate via
   * `mutate()`. That is either `succeeded.length > 0` (a write landed) or a result reporting
   * nothing at all in any array — which proves the rows are already in sync, so the row this
   * was launched from is stale. Never fired for a request that threw, or for one whose only
   * outcomes were refusals.
   *
   * Carries the request and the engine's result so the caller can patch its cache
   * optimistically BEFORE that refetch lands (`patchSubitemScanAfterResync`) — the full-page
   * rescan takes tens of seconds, and until 2026-09-10 the row kept showing the drift the
   * user had just fixed for all of them. */
  onResynced: (request: SubitemResyncRequest, result: ModalResult) => void;
}

/**
 * REMOVED: `pickWinningBoard`.
 *
 * This modal used to compute the winning board itself, ranking on `updatedAt` and
 * tie-breaking on board id, and its comment claimed the result was "guaranteed to be the
 * one the backend picks too" because both read the same `row`. That guarantee held only
 * while the two implementations agreed, and they stopped: `selectSourceBoard` gained
 * column-level `changed_at` ranking and an empty-and-never-written eligibility filter
 * (simb-integration-ms commit 6b3f101, deferred item I-1) and this copy was not updated.
 *
 * The divergence is not academic — it is exactly the case that fix was written for. Board
 * B's subitem is newer because of an unrelated edit; board A holds the only value this
 * column has ever had. The write correctly takes A. This modal previewed B. The outcome
 * was right and the preview was wrong, which is worse than either alone: the user confirms
 * one thing and receives another.
 *
 * The row now carries `winningBoardId`, computed by the engine with the same function the
 * write uses, so there is one implementation instead of two in independently-built
 * services. Read it; do not re-derive it.
 */
const boardName = (boards: BoardHeader[], boardId: string): string =>
  boardLabel(Object.fromEntries(boards.map((board) => [board.boardId, board.boardName])), boardId);

/**
 * The winning board's own last-updated time, rendered for the preview.
 *
 * Merge condition 2 requires the UI to show WHICH board wins AND its timestamp, so a user
 * can judge whether "most recently updated" means what they expect before committing —
 * "latest wins" is only a safe default if you can see what it resolved to.
 *
 * Falls back to the raw string rather than throwing or blanking: an unparseable timestamp
 * is still information, and the engine writes `updated_at ?? ""` so an empty one is
 * expected rather than exotic.
 */
const formatWinningTimestamp = (entry: ValueEntry): string | null => {
  if (!entry?.updatedAt) return null;
  const parsed = new Date(entry.updatedAt);
  if (Number.isNaN(parsed.getTime())) return entry.updatedAt;
  return parsed.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/** A board's text for the before/after table. `null` is an empty cell, rendered as "Empty". */
const valueTextFor = (entry: ValueEntry): string | null => {
  if (!entry) return null;
  if (entry.dropdownLabels && entry.dropdownLabels.length > 0) return entry.dropdownLabels.join(", ");
  return entry.text?.trim() ? entry.text : null;
};

/**
 * One row of the scan, as the shared before/after table reads it. The winner is the ENGINE's
 * `winningBoardId`, never recomputed here — see the note where `pickWinningBoard` used to live.
 * A board with no entry for this row has no subitem (or no such column) there, so it is shown
 * as "Not on this board" rather than as an empty value about to be overwritten.
 */
const toPreview = (row: SubitemScanRow, boards: BoardHeader[]): ResyncPreview => {
  const winner = row.winningBoardId ? row.valuesByBoard[row.winningBoardId] : null;
  return {
    columnTitle: row.columnKey === NAME_COLUMN_KEY ? "Subitem name" : row.columnTitle,
    latestBoardId: row.winningBoardId,
    latestChangedAt: formatWinningTimestamp(winner),
    rows: boards.map((board) => {
      const entry = row.valuesByBoard[board.boardId];
      return {
        boardId: board.boardId,
        boardName: board.boardName,
        currentText: valueTextFor(entry),
        missing: entry === null || entry === undefined,
      };
    }),
  };
};

/** `namesByBoard` is derived from the `__name__` row's `valuesByBoard` — the field is
 * `name`, not `text` (resyncSubitemName's own doc comment). */
const toNamesByBoard = (
  valuesByBoard: SubitemScanRow["valuesByBoard"],
): Record<string, { name: string; updatedAt: string }> =>
  Object.fromEntries(
    Object.entries(valuesByBoard)
      .filter((entry): entry is [string, NonNullable<ValueEntry>] => entry[1] !== null)
      .map(([boardId, entry]) => [boardId, { name: entry.text, updatedAt: entry.updatedAt }]),
  );

/** Resolves a failed entry's `columnKey` back to a human column title, when this request
 * carries enough rows to know it. Returns null for a board-level failure (no columnKey) or
 * one this request has no row for. */
const columnTitleFor = (request: SubitemResyncRequest, columnKey: string | undefined): string | null => {
  if (!columnKey) return null;
  const rows = request.kind === "subitem" ? request.rows : [request.row];
  return rows.find((row) => row.columnKey === columnKey)?.columnTitle ?? null;
};

const previewRows = (request: SubitemResyncRequest): SubitemScanRow[] =>
  request.kind === "subitem" ? request.rows.filter((row) => row.state !== "ok") : [request.row];

/** What the dialog is about, for its title and its outcome sentence. */
const requestLabel = (request: SubitemResyncRequest): string =>
  request.kind === "column"
    ? request.row.columnTitle
    : request.kind === "name"
      ? "Subitem name"
      : request.subitemName;

/**
 * A result that reports nothing at all — no board written, no board refused, no call rejected.
 * Reachable and benign rather than exotic (a stale scan, a double click, a teammate syncing
 * first), and it used to render an empty flex div with a "Done" button under it: the user
 * previewed a change, confirmed it, and was told nothing whatsoever.
 */
const isNoChangeResult = (result: ModalResult): boolean =>
  result.succeeded.length === 0 && result.failed.length === 0 && result.rejected.length === 0;

/** The drifting `__name__` row of a bulk request, if it has one. */
const bulkNameRow = (request: SubitemResyncSubitemRequest): SubitemScanRow | undefined =>
  request.rows.find((row) => row.columnKey === NAME_COLUMN_KEY && row.state !== "ok");

/** Everything a bulk request sends to `resync-subitem`: every row EXCEPT the name row, `ok`
 *  rows included (the controller filters those itself). */
const bulkColumnRows = (request: SubitemResyncSubitemRequest): SubitemScanRow[] =>
  request.rows.filter((row) => row.columnKey !== NAME_COLUMN_KEY);

/**
 * What a bulk confirm actually spans, said out loud. `name_drift` OVERRIDES `value_drift` in
 * the engine's classifier (subitem-classifier.ts:189-191), so a "Sync all" can legitimately
 * cover the name only, the columns only, or both — and the sentence must name whichever it is,
 * since the name and the columns travel to different endpoints.
 */
const bulkPreviewSentence = (request: SubitemResyncSubitemRequest): string => {
  const hasName = Boolean(bulkNameRow(request));
  const hasColumns = bulkColumnRows(request).some((row) => row.state !== "ok");
  if (hasColumns && hasName) {
    return `${request.subitemName}'s out-of-sync columns and its name will each be copied from the board that changed most recently.`;
  }
  if (hasColumns) {
    return `${request.subitemName}'s out-of-sync columns will each be copied from the board that changed most recently.`;
  }
  if (hasName) {
    return `${request.subitemName}'s name will be copied from the board that changed most recently.`;
  }
  return `${request.subitemName} has nothing left to sync.`;
};

/**
 * The Subitems tab's resync confirmation dialog. Reuses SyncLatestModal's interaction shape
 * (preview the change, Confirm/Cancel, a loading Confirm button) but is its own component —
 * SyncLatestModal's props are shaped around the item view's `columnValuesResync`, and bending
 * it to also carry a raw SubitemScanRow is how a shared component grows two personalities.
 *
 * This modal does not patch state itself — it hands the request and the engine's result to
 * `onResynced`, and the owner of the scan cache (SubitemsTable) patches the row from the
 * engine's own answer and revalidates behind it. Phase 3a already invalidates the server-side
 * flags cache on a successful write, so that refetch returns the truth; the patch is what the
 * user sees in the meantime.
 */
const SubitemResyncModal: React.FC<SubitemResyncModalProps> = ({ request, token, onClose, onResynced }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ModalResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Every value here is scoped to whichever request is currently open — never carried over
  // into the next one, and never written anywhere the plan forbids (localStorage, Redis,
  // Mongo): it lives only in this component's state for the life of one open dialog.
  useEffect(() => {
    setResult(null);
    setError(null);
    setLoading(false);
  }, [request]);

  const isOpen = request !== null;

  // A single-column request for a column type the engine always refuses. Only the `column` kind
  // can be wholly unwritable: a `name` request never is, and a bulk request's other columns
  // still are writable (those rows are annotated individually in the preview instead).
  const unwritableRequest =
    request?.kind === "column" && isUnwritableRow(request.row) ? request.row : null;

  const handleClose = () => {
    onClose();
  };

  /**
   * What the primary button will do, in the table's own terms: "Update 2 boards", or "Clear on
   * 1 board" when the latest change was a clear. A multi-column request sums the boards each
   * writable column touches, which is the number of writes the user is agreeing to.
   */
  const confirmLabel = (() => {
    if (!request || unwritableRequest) return "Confirm";
    const previews = previewRows(request)
      .filter((row) => !isUnwritableRow(row))
      .map((row) => toPreview(row, request.boards));
    if (previews.length === 1) return previewActionLabel(previews[0]);
    const boards = new Set(previews.flatMap((preview) => previewTargets(preview).map((row) => row.boardId)));
    return boards.size > 0 ? `Update ${boards.size} ${boards.size === 1 ? "board" : "boards"}` : "Confirm";
  })();

  /**
   * A bulk confirm dispatches BOTH endpoints the previewed changes actually need, because
   * `resync-subitem` cannot write a name: the `__name__` row's `columnIdsByBoard` is empty by
   * design, so `buildColumnWrites` produces nothing for it and the row is discarded without a
   * trace. Sending it there previewed a rename that never happened and then reported
   * "Sent to: <boards>" as though it had — and since `name_drift` OVERRIDES `value_drift`
   * (subitem-classifier.ts:189-191), that was the common case for this button, not an edge one.
   *
   * `allSettled`, not `all`: one endpoint failing must not discard the other's per-board result.
   * A call that rejects outright has no per-board detail to report, so it becomes a `rejected`
   * sentence — never silence, and never folded into `succeeded`.
   */
  const confirmBulk = async (
    bulkRequest: SubitemResyncSubitemRequest,
    activeToken: string,
  ): Promise<ModalResult> => {
    const nameRow = bulkNameRow(bulkRequest);
    const columnRows = bulkColumnRows(bulkRequest);
    const hasDriftingColumns = columnRows.some((row) => row.state !== "ok");

    // Explicitly parameterised: the request utils resolve `response?.data?.data` (`any`), so
    // without this both `.value`s would be `any` and every field read below would be unchecked.
    const [columnOutcome, nameOutcome] = await Promise.allSettled<ResyncResult | null>([
      hasDriftingColumns
        ? resyncSubitem(activeToken, bulkRequest.parentSyncId, bulkRequest.subitemSyncId, columnRows)
        : Promise.resolve(null),
      nameRow
        ? resyncSubitemName(
            activeToken,
            bulkRequest.parentSyncId,
            bulkRequest.subitemSyncId,
            toNamesByBoard(nameRow.valuesByBoard),
          )
        : Promise.resolve(null),
    ]);

    // Every call we actually made rejected: there is nothing partial to render, so this behaves
    // exactly like a single-endpoint failure — the retryable error, not a result panel that
    // would be empty apart from failure sentences.
    const attempted = [hasDriftingColumns, Boolean(nameRow)].filter(Boolean).length;
    const rejections = [columnOutcome, nameOutcome].filter(
      (outcome): outcome is PromiseRejectedResult => outcome.status === "rejected",
    );
    if (attempted > 0 && rejections.length === attempted) {
      throw rejections[0].reason;
    }

    const combined: ModalResult = { succeeded: [], failed: [], rejected: [] };

    if (columnOutcome.status === "rejected") {
      combined.rejected.push("The drifting columns could not be synced. Please try again.");
    } else if (columnOutcome.value) {
      combined.succeeded.push(...columnOutcome.value.succeeded);
      combined.failed.push(...columnOutcome.value.failed);
    }

    if (nameOutcome.status === "rejected") {
      combined.rejected.push("The subitem name could not be synced. Please try again.");
    } else if (nameOutcome.value) {
      combined.succeeded.push(...nameOutcome.value.succeeded);
      // Tagged with the synthetic name key so a name failure renders as "Board — Subitem name"
      // rather than as an untitled board-level failure indistinguishable from the column
      // batch's own. `columnTitleFor` resolves it off the request's rows, which still carry it.
      combined.failed.push(
        ...nameOutcome.value.failed.map((failure) => ({
          ...failure,
          columnKey: failure.columnKey ?? NAME_COLUMN_KEY,
        })),
      );
    }

    // The same board can succeed on both calls (its columns AND its name); "Sent to:" lists
    // boards, so it must name each one once — a duplicate is also a duplicate React key.
    combined.succeeded = Array.from(new Set(combined.succeeded));
    return combined;
  };

  const handleConfirm = async () => {
    if (!request || !token) return;
    setLoading(true);
    setError(null);
    try {
      let response: ModalResult;
      if (request.kind === "column") {
        const columnResponse: ResyncResult = await resyncSubitemColumn(
          token,
          request.parentSyncId,
          request.subitemSyncId,
          request.columnKey,
          request.row,
        );
        response = { ...columnResponse, rejected: [] };
      } else if (request.kind === "name") {
        const nameResponse: ResyncResult = await resyncSubitemName(
          token,
          request.parentSyncId,
          request.subitemSyncId,
          toNamesByBoard(request.row.valuesByBoard),
        );
        response = { ...nameResponse, rejected: [] };
      } else {
        response = await confirmBulk(request, token);
      }

      setResult(response);
      // Mirrors the engine's own flags-invalidation gate for these three endpoints
      // (subitem-resync.service.ts's `finishResync`): a write LANDED, so a refetch can
      // return something new. `failed.length === 0` is deliberately not part of this
      // condition — a board can be in both arrays at once (see the ResyncResult doc above).
      //
      // The second half fires on the "nothing needed to change" result: the server proved the
      // rows are already in sync, so the row this action was launched from is stale and must
      // refresh — without this the user is told nothing happened and then keeps looking at the
      // discrepancy that no longer exists.
      if (response.succeeded.length > 0 || isNoChangeResult(response)) {
        onResynced(request, response);
      }
    } catch (err: any) {
      logger.error(err?.message ?? "Error resyncing subitem", { error: err });
      setError("Something went wrong while syncing. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal id="subitem-resync" show={isOpen} onClose={handleClose} size="medium">
      <ModalBasicLayout>
        <ModalHeader title={request ? `Sync ${requestLabel(request)}` : "Sync"} className="px-8" />
        <ModalContent>
          {!request ? null : result ? (
            <ResyncOutcome
              subject={requestLabel(request)}
              updatedBoards={result.succeeded.map((boardId) => boardName(request.boards, boardId))}
              notUpdated={result.failed.map(
                (failure): ResyncFailureLine => ({
                  boardName: boardName(request.boards, failure.boardId),
                  columnTitle: columnTitleFor(request, failure.columnKey) ?? undefined,
                  reason: failure.reason,
                }),
              )}
              rejected={result.rejected}
            />
          ) : unwritableRequest ? (
            // Important 3: `translateColumnValue` refuses these column types 100% of the time,
            // so the old copy ("This will sync Related items to its most recently updated
            // value") promised a write that could not happen, with Confirm enabled and the
            // reason only arriving after the user committed. Confirm is disabled below.
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-small)" }}>
              <Text type="text1" element="p">
                {`${unwritableRequest.columnTitle} can't be synced between boards. Monday doesn't expose a usable value for ${unwritableRequest.columnType} columns, so there is nothing SIMB can copy across — this is a Monday platform limitation, not a failure.`}
              </Text>
              <Text type="text2" color="secondary" element="p">
                You can still update this column directly on each board.
              </Text>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-medium)" }}>
              {request.kind === "subitem" ? (
                <Text type="text1" element="p">
                  {bulkPreviewSentence(request)}
                </Text>
              ) : null}
              {previewRows(request).map((row) => {
                const preview = toPreview(row, request.boards);
                return (
                  <div key={row.columnKey} style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-small)" }}>
                    {request.kind === "subitem" ? (
                      <Text type="text1" weight="bold" element="p">
                        {preview.columnTitle}
                      </Text>
                    ) : null}
                    {/* A bulk request can carry an unwritable column alongside writable ones.
                        Confirm stays enabled — the others are genuinely syncable — but this row
                        must not be previewed as though it were going to be written. */}
                    {isUnwritableRow(row) ? (
                      <Text type="text2" color="secondary" element="p">
                        {`Monday doesn't expose a usable value for ${row.columnType} columns, so this one will be skipped.`}
                      </Text>
                    ) : (
                      <ResyncPreviewTable preview={preview} />
                    )}
                  </div>
                );
              })}
              <Text type="text2" color="secondary" element="p">
                This cannot be undone.
              </Text>
              {error ? (
                <Text type="text2" color="negative" element="p">
                  {error}
                </Text>
              ) : null}
            </div>
          )}
        </ModalContent>
      </ModalBasicLayout>
      <ModalFooter
        primaryButton={
          result
            ? { onClick: handleClose, text: "Done" }
            : {
                onClick: handleConfirm,
                text: confirmLabel,
                loading,
                // Same shape as the create path's own empty-targetBoardIds gate
                // (SubitemDestructiveModal.tsx): when the preview says the action cannot
                // happen, the button that would attempt it is disabled rather than left live.
                disabled: !token || unwritableRequest !== null,
              }
        }
        secondaryButton={result ? undefined : { onClick: handleClose, text: "Cancel", disabled: loading }}
      />
    </Modal>
  );
};

export default SubitemResyncModal;
